import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, Plus, X, Clock, Save, RotateCcw, CalendarDays, Link2, Loader2, CheckCircle2, Unplug, Video } from "lucide-react";

const WEEK_OPTIONS = [4, 8, 12];

const WD = [
  ["mon", "av.wd_mon"], ["tue", "av.wd_tue"], ["wed", "av.wd_wed"], ["thu", "av.wd_thu"],
  ["fri", "av.wd_fri"], ["sat", "av.wd_sat"], ["sun", "av.wd_sun"],
];
const WD_SHORT = { mon: "av.wds_mon", tue: "av.wds_tue", wed: "av.wds_wed", thu: "av.wds_thu", fri: "av.wds_fri", sat: "av.wds_sat", sun: "av.wds_sun" };
const TZS = ["Europe/Berlin", "Europe/Vienna", "Europe/Zurich", "Europe/London", "UTC", "America/New_York"];
const DEFAULT_SLOT = { start: "09:00", end: "17:00" };

function fmtDay(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }); }
  catch { return iso; }
}
function chunk(arr, n) { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; }

function SlotEditor({ slots, onChange, idPrefix }) {
  const t = useT();
  const update = (i, patch) => onChange(slots.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i) => onChange(slots.filter((_, idx) => idx !== i));
  const add = () => onChange([...slots, { ...DEFAULT_SLOT }]);
  return (
    <div className="space-y-2">
      {slots.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          <Input type="time" value={s.start} onChange={(e) => update(i, { start: e.target.value })} className="h-8 w-28" data-testid={`${idPrefix}-start-${i}`} />
          <span className="text-muted-foreground">–</span>
          <Input type="time" value={s.end} onChange={(e) => update(i, { end: e.target.value })} className="h-8 w-28" data-testid={`${idPrefix}-end-${i}`} />
          <button type="button" onClick={() => remove(i)} className="text-muted-foreground hover:text-foreground" data-testid={`${idPrefix}-remove-${i}`}>
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" className="gap-1 text-primary" onClick={add} data-testid={`${idPrefix}-add`}>
        <Plus className="h-3.5 w-3.5" /> {t("av.slot")}
      </Button>
    </div>
  );
}

export default function Availability() {
  const { user } = useAuth();
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [resolved, setResolved] = useState([]);
  const [editor, setEditor] = useState(null); // {date, available, slots, hasOverride}
  const [weeks, setWeeks] = useState(8);
  const [outlook, setOutlook] = useState(null);
  const [outlookBusy, setOutlookBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [zoomBusy, setZoomBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/coach/availability/v2?weeks=${weeks}`);
      setSettings(data?.settings || null);
      setResolved(data?.resolved || []);
    } catch (err) { toast.error(apiError(err, t("av.load_err"))); }
    finally { setLoading(false); }
  }, [weeks]);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  const loadOutlook = useCallback(async () => {
    try { const { data } = await api.get("/integrations/outlook/status"); setOutlook(data); }
    catch { setOutlook({ configured: false, connected: false }); }
  }, []);
  useEffect(() => { if (user?.role === "coach") loadOutlook(); }, [user, loadOutlook]);

  const loadZoom = useCallback(async () => {
    try { const { data } = await api.get("/integrations/zoom/status"); setZoom(data); }
    catch { setZoom({ configured: false, connected: false }); }
  }, []);
  useEffect(() => { if (user?.role === "coach") loadZoom(); }, [user, loadZoom]);

  useEffect(() => {
    const o = params.get("outlook");
    const z = params.get("zoom");
    if (o === "connected") { toast.success(t("av.outlook_connected")); loadOutlook(); }
    else if (o === "error") { toast.error(t("av.outlook_failed")); }
    if (z === "connected") { toast.success(t("zoom.t_connected")); loadZoom(); }
    else if (z === "error") { toast.error(t("zoom.t_error")); }
    if (o || z) { if (o) params.delete("outlook"); if (z) params.delete("zoom"); setParams(params, { replace: true }); }
  }, [params, setParams, loadOutlook, loadZoom]);

  const connectOutlook = async () => {
    setOutlookBusy(true);
    try {
      const { data } = await api.get("/integrations/outlook/start");
      if (data?.authorize_url) { window.location.href = data.authorize_url; return; }
    } catch (err) { toast.error(apiError(err, t("av.outlook_not_setup"))); }
    finally { setOutlookBusy(false); }
  };
  const disconnectOutlook = async () => {
    setOutlookBusy(true);
    try { await api.post("/integrations/outlook/disconnect"); toast.success(t("av.outlook_disconnected")); await loadOutlook(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setOutlookBusy(false); }
  };

  const connectZoom = async () => {
    setZoomBusy(true);
    try {
      const { data } = await api.get("/integrations/zoom/start");
      if (data?.authorize_url) { window.location.href = data.authorize_url; return; }
    } catch (err) { toast.error(apiError(err, t("zoom.not_setup"))); }
    finally { setZoomBusy(false); }
  };
  const disconnectZoom = async () => {
    setZoomBusy(true);
    try { await api.post("/integrations/zoom/disconnect"); toast.success(t("zoom.t_disconnected")); await loadZoom(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setZoomBusy(false); }
  };

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const mode = settings?.mode || "uniform";
  const timezone = settings?.timezone || "Europe/Berlin";
  const uniform = settings?.uniform_week || {};

  const putSettings = async (next) => {
    setSaving(true);
    try {
      const body = {
        mode: next.mode ?? mode,
        timezone: next.timezone ?? timezone,
        uniform_week: next.uniform_week ?? uniform,
        overrides: next.overrides ?? settings?.overrides ?? {},
        individual: next.individual ?? settings?.individual ?? {},
      };
      const { data } = await api.put("/coach/availability/v2", body);
      setSettings(data?.settings || null);
      await load();
      toast.success(t("av.saved"));
    } catch (err) { toast.error(apiError(err, t("av.save_failed"))); }
    finally { setSaving(false); }
  };

  const setDay = (key, patch) => {
    const cur = uniform[key] || { available: false, slots: [] };
    const nextDay = { ...cur, ...patch };
    if (patch.available && (!nextDay.slots || nextDay.slots.length === 0)) nextDay.slots = [{ ...DEFAULT_SLOT }];
    setSettings((s) => ({ ...s, uniform_week: { ...s.uniform_week, [key]: nextDay } }));
  };

  const openDay = (d) => setEditor({ date: d.date, available: d.available, slots: (d.slots || []).map((s) => ({ ...s })), hasOverride: d.is_custom || mode === "individual" });

  const saveDay = async () => {
    try {
      await api.patch(`/coach/availability/v2/day/${editor.date}`, { available: editor.available, slots: editor.available ? editor.slots : [] });
      toast.success(t("av.day_updated", { day: fmtDay(editor.date) }));
      setEditor(null);
      await load();
    } catch (err) { toast.error(apiError(err, t("av.day_save_failed"))); }
  };
  const resetDay = async () => {
    try {
      await api.delete(`/coach/availability/v2/day/${editor.date}`);
      toast.success(t("av.day_reset", { day: fmtDay(editor.date) }));
      setEditor(null);
      await load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const weekRows = chunk(resolved, 7);

  return (
    <div data-testid="coach-availability-page">
      <Seo title={t("av.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="availability-back">
        <ChevronLeft className="h-4 w-4" /> {t("earn.back")}
      </Link>
      <PageHeader eyebrow={t("av.eyebrow")} title={t("av.title")} subtitle={t("av.subtitle")} />

      {/* Outlook calendar connection */}
      {outlook && (
        <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="outlook-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0F6CBD]/10 text-[#0F6CBD]"><CalendarDays className="h-5 w-5" /></span>
            <div>
              <p className="text-sm font-semibold text-foreground">{t("av.outlook_cal")}</p>
              {!outlook.configured ? (
                <p className="text-xs text-muted-foreground">{t("av.outlook_soon")}</p>
              ) : outlook.connected ? (
                <p className="flex items-center gap-1.5 text-xs text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> {t("av.connected")}{outlook.email ? ` · ${outlook.email}` : ""}{outlook.needs_reconnect ? t("av.reconnect_needed") : ""}</p>
              ) : (
                <p className="text-xs text-muted-foreground">{t("av.outlook_hint")}</p>
              )}
            </div>
          </div>
          {outlook.configured && (
            outlook.connected && !outlook.needs_reconnect ? (
              <Button variant="outline" className="gap-1.5 rounded-full" disabled={outlookBusy} onClick={disconnectOutlook} data-testid="outlook-disconnect">
                {outlookBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />} {t("av.disconnect")}
              </Button>
            ) : (
              <Button className="gap-1.5 rounded-full" disabled={outlookBusy} onClick={connectOutlook} data-testid="outlook-connect">
                {outlookBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} {outlook.needs_reconnect ? t("av.reconnect") : t("av.outlook_connect")}
              </Button>
            )
          )}
        </div>
      )}

      {/* Zoom video connection */}
      {zoom && (
        <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="zoom-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#2D8CFF]/10 text-[#2D8CFF]"><Video className="h-5 w-5" /></span>
            <div>
              <p className="text-sm font-semibold text-foreground">{t("zoom.title")}</p>
              {!zoom.configured ? (
                <p className="text-xs text-muted-foreground">{t("zoom.soon")}</p>
              ) : zoom.connected ? (
                <p className="flex items-center gap-1.5 text-xs text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> {t("zoom.connected")}{zoom.zoom_email ? ` · ${zoom.zoom_email}` : (zoom.zoom_account_name ? ` · ${zoom.zoom_account_name}` : "")}</p>
              ) : (
                <p className="text-xs text-muted-foreground">{t("zoom.hint")}</p>
              )}
            </div>
          </div>
          {zoom.configured && (
            zoom.connected ? (
              <Button variant="outline" className="gap-1.5 rounded-full" disabled={zoomBusy} onClick={disconnectZoom} data-testid="zoom-disconnect">
                {zoomBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />} {t("zoom.disconnect")}
              </Button>
            ) : (
              <Button className="gap-1.5 rounded-full" disabled={zoomBusy} onClick={connectZoom} data-testid="zoom-connect">
                {zoomBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} {t("zoom.connect")}
              </Button>
            )
          )}
        </div>
      )}

      {loading || !settings ? (
        <div className="space-y-4"><Skeleton className="h-16 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /></div>
      ) : (
        <div className="space-y-6">
          {/* Mode + timezone */}
          <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-border bg-card p-4">
            <div className="flex gap-2">
              {[["uniform", t("av.mode_uniform")], ["individual", t("av.mode_individual")]].map(([m, lbl]) => (
                <button key={m} type="button" onClick={() => mode !== m && putSettings({ mode: m })} data-testid={`avail-mode-${m}`}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${mode === m ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{t("av.timezone")}</span>
              <select
                value={TZS.includes(timezone) ? timezone : "custom"}
                onChange={(e) => putSettings({ timezone: e.target.value === "custom" ? timezone : e.target.value })}
                className="h-9 rounded-lg border border-border bg-background px-2 text-sm"
                data-testid="avail-timezone"
              >
                {TZS.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                {!TZS.includes(timezone) && <option value="custom">{timezone}</option>}
              </select>
            </div>
            <p className="text-xs text-muted-foreground">
              {mode === "uniform" ? t("av.uniform_hint", { weeks }) : t("av.individual_hint")}
            </p>
          </div>

          {/* Weekly template (uniform only) */}
          {mode === "uniform" && (
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="avail-weekly-template">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">{t("av.week_template")}</p>
                <Button size="sm" className="gap-1.5 rounded-full" onClick={() => putSettings({})} disabled={saving} data-testid="avail-save-template">
                  <Save className="h-4 w-4" /> {saving ? t("av.saving") : t("av.save_template")}
                </Button>
              </div>
              <div className="space-y-2">
                {WD.map(([key, label]) => {
                  const day = uniform[key] || { available: false, slots: [] };
                  return (
                    <div key={key} className="rounded-xl border border-border p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground">{t(label)}</span>
                        <Switch checked={!!day.available} onCheckedChange={(v) => setDay(key, { available: v })} data-testid={`avail-day-${key}`} />
                      </div>
                      {day.available && (
                        <div className="mt-2">
                          <SlotEditor slots={day.slots || []} onChange={(slots) => setDay(key, { slots })} idPrefix={`avail-${key}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 8-week horizon */}
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="avail-horizon">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <CalendarDays className="h-4 w-4 text-primary" />
              <p className="text-sm font-semibold text-foreground">{t("av.next_weeks", { weeks })}</p>
              <span className="text-xs text-muted-foreground">{t("av.tap_hint")}</span>
              <div className="ml-auto flex items-center gap-1" data-testid="avail-weeks-row">
                <span className="mr-1 text-xs text-muted-foreground">{t("av.view")}</span>
                {WEEK_OPTIONS.map((w) => (
                  <button key={w} type="button" onClick={() => setWeeks(w)} data-testid={`avail-weeks-${w}`}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${weeks === w ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-accent"}`}>
                    {w === 4 ? t("av.month") : t("av.weeks_short", { w })}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-2 grid grid-cols-7 gap-1.5 text-center text-xs font-medium text-muted-foreground">
              {WD.map(([k]) => <span key={k}>{t(WD_SHORT[k])}</span>)}
            </div>
            <div className="space-y-1.5">
              {weekRows.map((wk, wi) => (
                <div key={wi} className="grid grid-cols-7 gap-1.5">
                  {wk.map((d) => (
                    <button
                      key={d.date}
                      type="button"
                      disabled={d.is_past}
                      onClick={() => openDay(d)}
                      data-testid={`avail-cell-${d.date}`}
                      className={`flex flex-col items-center rounded-lg border p-1.5 text-center transition-colors ${
                        d.is_past ? "cursor-not-allowed border-transparent bg-secondary/40 text-muted-foreground/40"
                          : d.available ? "border-primary/40 bg-primary/5 hover:bg-primary/10"
                          : "border-border bg-card hover:bg-accent"
                      }`}
                    >
                      <span className="text-[11px] text-muted-foreground">{fmtDay(d.date)}</span>
                      <span className={`mt-1 h-1.5 w-1.5 rounded-full ${d.available ? "bg-emerald-500" : "bg-slate-300"}`} />
                      {d.available && <span className="mt-0.5 text-[10px] text-muted-foreground">{(d.slots || []).length}×</span>}
                      {d.is_custom && <span className="mt-0.5 rounded bg-amber-100 px-1 text-[9px] font-semibold text-amber-700">{t("av.custom")}</span>}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Day editor */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent data-testid="avail-day-dialog">
          <DialogHeader>
            <DialogTitle>{editor ? t("av.adjust_day", { day: fmtDay(editor.date) }) : ""}</DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-xl border border-border p-3">
                <span className="text-sm font-medium text-foreground">{t("av.available")}</span>
                <Switch
                  checked={editor.available}
                  onCheckedChange={(v) => setEditor((e) => ({ ...e, available: v, slots: v && (!e.slots || e.slots.length === 0) ? [{ ...DEFAULT_SLOT }] : e.slots }))}
                  data-testid="avail-dialog-available"
                />
              </div>
              {editor.available && (
                <SlotEditor slots={editor.slots || []} onChange={(slots) => setEditor((e) => ({ ...e, slots }))} idPrefix="avail-dialog" />
              )}
            </div>
          )}
          <DialogFooter className="flex-row justify-between sm:justify-between">
            {editor?.hasOverride ? (
              <Button variant="ghost" className="gap-1.5 text-muted-foreground" onClick={resetDay} data-testid="avail-dialog-reset">
                <RotateCcw className="h-4 w-4" /> {t("av.reset")}
              </Button>
            ) : <span />}
            <Button className="gap-1.5 rounded-full" onClick={saveDay} data-testid="avail-dialog-save">
              <Save className="h-4 w-4" /> {t("av.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
